import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp, staggerItem, cardVariants } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';
import Button from './Button';

const Quality = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  const [activeTab, setActiveTab] = useState('standards');
  const [isFlowerOpen, setIsFlowerOpen] = useState(false);
  const [isFlowerFixed, setIsFlowerFixed] = useState(false);
  const sectionRef = React.useRef(null);
  const flowerRef = React.useRef(null);

  const qualityTabs = [
    { id: 'standards', name: 'Standards & Certifications', icon: '🏆' },
    { id: 'testing', name: 'Testing Equipment', icon: '🔬' },
    { id: 'compliance', name: 'Compliance Records', icon: '📋' }
  ];

  // Handle sticky flower behavior like Products section
  React.useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !flowerRef.current) return;
      
      const section = sectionRef.current;
      const sectionRect = section.getBoundingClientRect();
      
      // Check if section is in viewport
      const sectionInView = sectionRect.top < window.innerHeight && sectionRect.bottom > 0;
      
      // Check if flower should be fixed (when section top goes above viewport)
      const shouldBeFixed = sectionRect.top < 16 && sectionInView;
      
      setIsFlowerFixed(shouldBeFixed);
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const certifications = [
    {
      title: 'ISO 9001:2015',
      description: 'Quality Management System certification ensuring consistent quality processes and customer satisfaction.',
      validity: 'Valid until 2026',
      scope: 'Steel production and quality management',
      issuer: 'International Standards Organization'
    },
    {
      title: 'ASTM A615',
      description: 'Standard specification for deformed and plain carbon-steel bars for concrete reinforcement.',
      validity: 'Current',
      scope: 'Rebar production compliance',
      issuer: 'ASTM International'
    },
    {
      title: 'Local BDS Standards',
      description: 'Bangladesh Standards for steel products ensuring compliance with national requirements.',
      validity: 'Current',
      scope: 'All steel products',
      issuer: 'Bangladesh Standards'
    },
    {
      title: 'Environmental Compliance',
      description: 'Environmental management and emission control certifications for sustainable operations.',
      validity: 'Valid until 2025',
      scope: 'Manufacturing operations',
      issuer: 'Environmental Authority'
    }
  ];

  const testingEquipment = [
    {
      name: 'Universal Testing Machine',
      capacity: '600 kN',
      purpose: 'Tensile strength, yield strength, and elongation testing',
      accuracy: '±0.5% of reading',
      features: ['Automated testing', 'Digital recording', 'Statistical analysis', 'Compliance reporting']
    },
    {
      name: 'Optical Emission Spectrometer',
      capacity: 'Multi-element',
      purpose: 'Chemical composition analysis of steel samples',
      accuracy: '±0.001% for carbon',
      features: ['Rapid analysis', 'Multi-element detection', 'Database storage', 'Trend analysis']
    },
    {
      name: 'Hardness Testing Equipment',
      capacity: 'Brinell/Rockwell',
      purpose: 'Surface hardness measurement and verification',
      accuracy: '±1 HB/HR unit',
      features: ['Multiple scales', 'Automatic indentation', 'Digital display', 'Data logging']
    },
    {
      name: 'Metallographic Microscope',
      capacity: '1000x magnification',
      purpose: 'Microstructure analysis and grain size determination',
      accuracy: 'ASTM grain size',
      features: ['Digital imaging', 'Grain analysis', 'Inclusion rating', 'Report generation']
    },
    {
      name: 'Dimensional Measuring Tools',
      capacity: 'Various ranges',
      purpose: 'Precise dimensional verification and tolerance checking',
      accuracy: '±0.01mm',
      features: ['Caliper measurements', 'Profile checking', 'Weight verification', 'Geometric tolerance']
    }
  ];

  const qualityMetrics = [
    { metric: 'Product Conformity Rate', value: '99.8%', trend: 'up' },
    { metric: 'Customer Satisfaction', value: '98.5%', trend: 'up' },
    { metric: 'First-Pass Quality', value: '97.2%', trend: 'stable' },
    { metric: 'On-Time Delivery', value: '99.1%', trend: 'up' }
  ];

  const TabContent = ({ tabId, colors }) => {
    switch (tabId) {
      case 'standards':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.title}
                variants={staggerItem}
                initial="initial"
                animate="animate"
                transition={{ delay: index * 0.1 }}
              >
                <Card hover={true} glass={true} className="p-6 h-full">
                  <div className="flex items-start justify-between mb-4">
                    <h4 className={`text-lg font-semibold ${colors.text.primary}`}>{cert.title}</h4>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <p className={`${colors.text.secondary} text-sm mb-4 leading-relaxed`}>
                    {cert.description}
                  </p>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className={colors.text.muted}>Validity:</span>
                      <span className="text-primary-400 font-medium">{cert.validity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={colors.text.muted}>Scope:</span>
                      <span className={colors.text.primary}>{cert.scope}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={colors.text.muted}>Issued by:</span>
                      <span className={colors.text.primary}>{cert.issuer}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        );

      case 'testing':
        return (
          <div className="space-y-6">
            {testingEquipment.map((equipment, index) => (
              <motion.div
                key={equipment.name}
                variants={staggerItem}
                initial="initial"
                animate="animate"
                transition={{ delay: index * 0.1 }}
              >
                <Card hover={true} className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                      <h4 className={`text-lg font-semibold ${colors.text.primary} mb-2`}>{equipment.name}</h4>
                      <p className={`${colors.text.secondary} text-sm mb-4`}>{equipment.purpose}</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className={colors.text.muted}>Capacity:</span>
                          <div className="text-primary-400 font-medium">{equipment.capacity}</div>
                        </div>
                        <div>
                          <span className={colors.text.muted}>Accuracy:</span>
                          <div className="text-primary-400 font-medium">{equipment.accuracy}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className={`${colors.text.primary} font-medium mb-3 text-sm`}>Key Features:</h5>
                      <ul className="space-y-1">
                        {equipment.features.map((feature, idx) => (
                          <li key={feature} className={`flex items-center ${colors.text.muted} text-xs`}>
                            <div className="w-1 h-1 bg-primary-400 rounded-full mr-2"></div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        );

      case 'compliance':
        return (
          <div className="space-y-8">
            {/* Quality Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {qualityMetrics.map((metric, index) => (
                <motion.div
                  key={metric.metric}
                  variants={staggerItem}
                  initial="initial"
                  animate="animate"
                  transition={{ delay: index * 0.1 }}
                >
                  <Card gradient={true} className="p-6 text-center">
                    <div className={`text-2xl font-bold ${colors.text.primary} mb-1`}>{metric.value}</div>
                    <div className={`${colors.text.secondary} text-sm mb-2`}>{metric.metric}</div>
                    <div className={`flex items-center justify-center text-xs ${
                      metric.trend === 'up' ? 'text-green-400' : 
                      metric.trend === 'down' ? 'text-red-400' : 'text-neutral-400'
                    }`}>
                      {metric.trend === 'up' && (
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
                        </svg>
                      )}
                      {metric.trend === 'stable' && (
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                        </svg>
                      )}
                      {metric.trend === 'up' ? 'Improving' : metric.trend === 'down' ? 'Declining' : 'Stable'}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Compliance Documentation */}
            <Card glass={true} className="p-8">
              <h4 className={`text-xl font-semibold ${colors.text.primary} mb-6`}>Compliance Documentation</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h5 className={`${colors.text.primary} font-medium mb-4`}>Available Documents</h5>
                  <ul className="space-y-3">
                    {[
                      'Mill Test Certificates (MTC)',
                      'Chemical Analysis Reports',
                      'Mechanical Property Reports',
                      'Dimensional Inspection Reports',
                      'Quality Control Procedures',
                      'Calibration Certificates'
                    ].map((doc, index) => (
                      <motion.li
                        key={doc}
                        className={`flex items-center justify-between ${colors.text.secondary} text-sm`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <span>{doc}</span>
                        <Button variant="text" size="sm">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download
                        </Button>
                      </motion.li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h5 className={`${colors.text.primary} font-medium mb-4`}>Traceability System</h5>
                  <p className={`${colors.text.secondary} text-sm mb-4 leading-relaxed`}>
                    Complete traceability from raw material to finished product ensures 
                    full accountability and quality verification at every stage.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-primary-400">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Batch tracking from billet to product
                    </div>
                    <div className="flex items-center text-primary-400">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Process parameter recording
                    </div>
                    <div className="flex items-center text-primary-400">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Quality test results linkage
                    </div>
                    <div className="flex items-center text-primary-400">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Customer delivery records
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Section id="quality" background="texture" className="relative" ref={sectionRef}>
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
          Quality & <span className="text-primary-400">Compliance</span>
        </h2>
        <p className={`text-body-lg ${colors.text.secondary} max-w-3xl mx-auto leading-relaxed`}>
          Our commitment to excellence is backed by rigorous quality systems, 
          advanced testing capabilities, and comprehensive compliance management.
        </p>
      </motion.div>

      {/* Desktop Tab Navigation - Hidden on mobile */}
      <motion.div
        className="hidden lg:flex flex-wrap justify-center gap-4 mb-12"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        {qualityTabs.map((tab) => (
          <motion.button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center space-x-3 ${
              activeTab === tab.id
                ? 'bg-primary-400 text-black font-semibold shadow-magnetic'
                : `${colors.bg.muted} ${colors.text.secondary} hover:${colors.bg.card} hover:${colors.text.primary}`
            }`}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="text-xl">{tab.icon}</span>
            <span>{tab.name}</span>
          </motion.button>
        ))}
      </motion.div>

      {/* Mobile Flower Navigation - Only visible on mobile */}
      <div className="lg:hidden">
        {/* Placeholder when flower is fixed to maintain layout */}
        {isFlowerFixed && <div className="mb-24"></div>}
        
        {/* Dynamic Positioning Container */}
        <div 
          ref={flowerRef}
          className={`${
            isFlowerFixed 
              ? 'fixed left-1/2 transform -translate-x-1/2 z-50' 
              : 'relative mb-12'
          } transition-all duration-500 ease-out`}
          style={isFlowerFixed ? { 
            top: '60px'
          } : {}}
        >
          <motion.div
            className={`relative flex justify-center items-center ${isFlowerFixed ? 'h-16 w-16' : 'h-32 w-full'} transition-all duration-300`}
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {/* Background Circle for Better Visual Context - Hidden when fixed */}
            {!isFlowerFixed && <div className={`absolute w-24 h-24 rounded-full ${colors.bg.muted}/30 blur-xl`}></div>}
            
            {/* Center Circle Button */}
            <motion.button
              onClick={() => setIsFlowerOpen(!isFlowerOpen)}
              className={`relative z-20 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ${
                isFlowerOpen 
                  ? 'bg-gradient-to-br from-primary-400 to-primary-600 text-black shadow-[0_8px_32px_rgba(247,148,29,0.4)]' 
                  : isFlowerFixed
                  ? 'bg-black/80 text-white border-2 border-primary-400/30 shadow-[0_8px_32px_rgba(0,0,0,0.3),0_0_0_1px_rgba(247,148,29,0.1),inset_0_1px_0_rgba(255,255,255,0.1)] backdrop-blur-md'
                  : `${colors.bg.card} ${colors.text.primary} border-2 ${colors.border.default} shadow-xl hover:shadow-2xl`
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              animate={{ 
                rotate: isFlowerOpen ? 45 : 0,
                boxShadow: isFlowerOpen 
                  ? "0 8px 32px rgba(247, 148, 29, 0.4)" 
                  : "0 4px 20px rgba(0, 0, 0, 0.1)"
              }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <AnimatePresence mode="wait">
                {isFlowerOpen ? (
                  <motion.svg 
                    key="plus-icon"
                    className="w-6 h-6" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: 1.1, rotate: 0 }}
                    exit={{ scale: 0, rotate: 45 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </motion.svg>
                ) : (
                  <motion.span
                    key="tab-icon"
                    className="text-2xl"
                    initial={{ scale: 0, rotate: 45 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: -45 }}
                    transition={{ duration: 0.3 }}
                  >
                    {qualityTabs.find(tab => tab.id === activeTab)?.icon}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Flower Petals - Tab Buttons */}
            <AnimatePresence>
              {isFlowerOpen && qualityTabs.map((tab, index) => {
                // Calculate rotation to place active tab at top
                const activeTabIndex = qualityTabs.findIndex(t => t.id === activeTab);
                const rotationOffset = activeTabIndex * 120; // Rotate by 120 degrees per step
                const angle = (index * 120) - 90 - rotationOffset; // Subtract rotation to move active to top
                const radius = 60; // Increased distance for better spacing
                const x = Math.cos((angle * Math.PI) / 180) * radius;
                const y = Math.sin((angle * Math.PI) / 180) * radius;

                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setIsFlowerOpen(false);
                    }}
                    className={`absolute w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 z-10 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-br from-primary-400 to-primary-600 text-black shadow-[0_6px_24px_rgba(247,148,29,0.3)] border-2 border-primary-300'
                        : `${colors.bg.card} ${colors.text.primary} border-2 ${colors.border.default} shadow-xl hover:shadow-2xl hover:border-primary-400/50`
                    }`}
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                      backdropFilter: 'blur(12px)',
                    }}
                    initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                    animate={{ 
                      scale: 1, 
                      opacity: 1,
                      x: x,
                      y: y,
                      transition: { 
                        delay: isFlowerOpen ? index * 0.15 : 0,
                        type: "spring",
                        stiffness: 300,
                        damping: 25,
                        duration: 0.6
                      }
                    }}
                    exit={{ 
                      scale: 0, 
                      opacity: 0,
                      x: 0,
                      y: 0,
                      transition: { 
                        delay: (qualityTabs.length - index - 1) * 0.08,
                        duration: 0.25
                      }
                    }}
                    whileHover={{ 
                      scale: 1.1, 
                      y: -3,
                      boxShadow: activeTab === tab.id 
                        ? "0 8px 32px rgba(247, 148, 29, 0.4)" 
                        : "0 8px 25px rgba(0, 0, 0, 0.15)"
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="text-base">{tab.icon}</span>
                  </motion.button>
                );
              })}
            </AnimatePresence>

            {/* Connection Lines Between Center and Petals */}
            <AnimatePresence>
              {isFlowerOpen && qualityTabs.map((tab, index) => {
                // Use same rotation calculation as petals
                const activeTabIndex = qualityTabs.findIndex(t => t.id === activeTab);
                const rotationOffset = activeTabIndex * 120;
                const angle = (index * 120) - 90 - rotationOffset;
                const innerRadius = 32; // Start from center button edge
                const outerRadius = 48; // End before petal button
                const x1 = Math.cos((angle * Math.PI) / 180) * innerRadius;
                const y1 = Math.sin((angle * Math.PI) / 180) * innerRadius;
                const x2 = Math.cos((angle * Math.PI) / 180) * outerRadius;
                const y2 = Math.sin((angle * Math.PI) / 180) * outerRadius;

                return (
                  <motion.div
                    key={`line-${tab.id}`}
                    className="absolute w-0.5 bg-gradient-to-r from-primary-400 to-primary-600 z-5"
                    style={{
                      left: '50%',
                      top: '50%',
                      height: `${Math.sqrt((x2-x1)**2 + (y2-y1)**2)}px`,
                      transformOrigin: 'top center',
                      transform: `translate(-50%, ${y1}px) rotate(${angle + 90}deg)`,
                    }}
                    initial={{ scaleY: 0, opacity: 0 }}
                    animate={{ 
                      scaleY: 1, 
                      opacity: 0.6,
                      transition: { 
                        delay: index * 0.15 + 0.2,
                        duration: 0.3
                      }
                    }}
                    exit={{ 
                      scaleY: 0, 
                      opacity: 0,
                      transition: { duration: 0.2 }
                    }}
                  />
                );
              })}
            </AnimatePresence>

          </motion.div>
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="mb-16"
        >
          <TabContent tabId={activeTab} colors={colors} />
        </motion.div>
      </AnimatePresence>

      {/* CTA Section */}
      <motion.div
        className="text-center"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        transition={{ delay: 0.6 }}
      >
        <Card glass={true} className="inline-block p-8 max-w-2xl">
          <h3 className={`text-xl font-semibold ${colors.text.primary} mb-4`}>
            Quality Documentation Available
          </h3>
          <p className={`${colors.text.secondary} mb-6`}>
            Request complete quality documentation packages including certificates, 
            test reports, and compliance records for your projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary" 
              magnetic={true}
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Request Quality Package
            </Button>
            <Button variant="ghost">
              View Sample Certificates
            </Button>
          </div>
        </Card>
      </motion.div>
    </Section>
  );
};

export default Quality;